import Link from "next/link";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { FaArrowRight } from "react-icons/fa";

const DEFAULT_CALENDLY_URL =
  "https://calendly.com/webdifference/nouvelle-reunion?hide_gdpr_banner=1&background_color=282828&text_color=ffffff&primary_color=71ddae&month=2025-12";

const HERO_CTA_BASE_CLASSNAME =
  "group relative isolate inline-flex items-center gap-2.5 overflow-hidden rounded-full font-semibold shadow-[0_12px_32px_-12px_rgba(0,0,0,0.45)] transition disabled:cursor-not-allowed disabled:opacity-50";

const HERO_CTA_SIZES = {
  default:
    "min-h-[56px] gap-3 py-2 pl-6 pr-2 text-[15px] sm:min-h-[60px] sm:pl-7 sm:text-base",
  compact: "min-h-0 py-3 pl-5 pr-2 text-sm",
} as const;

type HeroCtaSize = keyof typeof HERO_CTA_SIZES;

const HERO_CTA_VARIANTS = {
  dark: "bg-[#1C1C1C] text-white hover:bg-black",
  light:
    "border border-zinc-200/80 bg-white text-[#1C1C1C] shadow-[0_12px_32px_-12px_rgba(0,0,0,0.18)] hover:bg-zinc-50",
} as const;

type HeroCtaVariant = keyof typeof HERO_CTA_VARIANTS;

type HeroCtaButtonProps = {
  children: ReactNode;
  href?: string;
  shineDelay?: string;
  className?: string;
  variant?: HeroCtaVariant;
  size?: HeroCtaSize;
} & (
  | { as?: "link"; href?: string }
  | ({
      as: "button";
    } & Pick<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "disabled" | "onClick">)
);

function HeroCtaContent({
  children,
  shineDelay,
  variant,
  size,
}: {
  children: ReactNode;
  shineDelay: string;
  variant: HeroCtaVariant;
  size: HeroCtaSize;
}) {
  const isLight = variant === "light";
  const isCompact = size === "compact";

  return (
    <>
      <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-full">
        <span
          className="hero-cta-metal-shine-track absolute -inset-y-6 left-0 h-[calc(100%+3rem)] w-[42%] bg-[linear-gradient(105deg,transparent_0%,rgba(255,255,255,0.04)_28%,rgba(255,255,255,0.2)_44%,rgba(255,255,255,0.5)_50%,rgba(255,255,255,0.2)_56%,rgba(255,255,255,0.04)_72%,transparent_100%)]"
          style={{ animationDelay: shineDelay }}
        />
      </span>
      <span className="relative z-10">{children}</span>
      <span
        className={`hero-cta-arrow-pulse relative z-10 inline-flex shrink-0 items-center justify-center ${
          isCompact ? "h-5 w-5" : "h-10 w-10 sm:h-11 sm:w-11"
        }`}
        style={{ animationDelay: shineDelay }}
      >
        <span
          aria-hidden
          className={`absolute inset-0 rounded-full ${isLight ? "bg-[#1C1C1C]" : "bg-white"}`}
        />
        <span className="relative inline-flex -rotate-45 items-center justify-center">
          <span
            className="hero-cta-arrow-spin inline-flex items-center justify-center"
            style={{ animationDelay: shineDelay }}
          >
            <FaArrowRight
              className={`${isCompact ? "text-[9px]" : "text-sm sm:text-base"} ${isLight ? "text-white" : "text-[#1C1C1C]"}`}
              aria-hidden="true"
            />
          </span>
        </span>
      </span>
    </>
  );
}

export function HeroCtaButton({
  children,
  href = DEFAULT_CALENDLY_URL,
  shineDelay = "0s",
  className = "",
  variant = "dark",
  size = "default",
  ...props
}: HeroCtaButtonProps) {
  const mergedClassName =
    `${HERO_CTA_BASE_CLASSNAME} ${HERO_CTA_SIZES[size]} ${HERO_CTA_VARIANTS[variant]} ${className}`.trim();

  if ("as" in props && props.as === "button") {
    const { as: _as, type = "button", disabled, onClick } = props;

    return (
      <button
        type={type}
        disabled={disabled}
        onClick={onClick}
        className={mergedClassName}
      >
        <HeroCtaContent shineDelay={shineDelay} variant={variant} size={size}>
          {children}
        </HeroCtaContent>
      </button>
    );
  }

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={mergedClassName}
      suppressHydrationWarning
    >
      <HeroCtaContent shineDelay={shineDelay} variant={variant} size={size}>
        {children}
      </HeroCtaContent>
    </Link>
  );
}

export { DEFAULT_CALENDLY_URL };
